/**
 * The payment client-action driver seam (DNKADM-0245, extracted from
 * `BuyMembershipOrderDialog`).
 *
 * A `PENDING` order can come back with a `paymentAction` the CLIENT has to carry out
 * before the payment can settle — a redirect (which any dialog can drive itself with
 * `window.location.assign`) or a PSP-SDK **client action**, which only the host app can
 * perform. Buyer verification (3-D Secure) for a card entered at the desk is NOT handled
 * here: since DNKADM-0487 Square runs it inside `card.tokenize(verificationDetails)` in
 * `CardPaymentField`, before any order is placed. This driver covers only an action an
 * order hands back AFTER it was placed; no such PSP action is registered, so the default
 * driver REFUSES ({@link UnsupportedPaymentActionError}) rather than silently pretending
 * the action was carried out.
 *
 * The registry is a module-level singleton with a test setter, exactly as it was
 * while it lived in `BuyMembershipOrderDialog` — moved here verbatim so BOTH the buy
 * and the sell checkout drive the SAME registered driver instead of duplicating the
 * singleton (which would give the two dialogs different, silently-diverging seams).
 *
 * Callers resolve an action in this order (see `BuyMembershipOrderDialog`'s
 * `drivePaymentAction`): a top-level redirect first, then a nested `clientAction`
 * (its own redirect first, else {@link getPaymentActionDriver}). What each caller
 * does when the driver refuses is the CALLER's policy — the Buy dialog surfaces an
 * explicit failure, the sell cart falls through to its authoritative order re-read.
 */

/**
 * One PSP client action, carried verbatim off the order's `paymentAction`. Module-internal:
 * it only shapes {@link MembershipPaymentActionDriver}'s parameter, which callers get
 * structurally — exporting it would be an unused export (DNKADM-0249).
 */
type MembershipPaymentClientAction = Record<string, unknown>;

/** Performs a PSP client action (e.g. a buyer verification challenge). */
export type MembershipPaymentActionDriver = (
  clientAction: MembershipPaymentClientAction,
) => void | Promise<void>;

/** Thrown when no driver can carry out the client action the order demands. */
export class UnsupportedPaymentActionError extends Error {
  constructor() {
    super('UNSUPPORTED_PAYMENT_ACTION');
    this.name = 'UnsupportedPaymentActionError';
  }
}

/**
 * The default driver: refuse. A challenge that cannot be answered must never be
 * reported as answered — the order stays where the server put it. Module-internal: it is
 * reached through {@link getPaymentActionDriver} / the test setter's `null`, never imported
 * directly (DNKADM-0249).
 */
const unsupportedPaymentActionDriver: MembershipPaymentActionDriver = () => {
  throw new UnsupportedPaymentActionError();
};

let membershipPaymentActionDriver: MembershipPaymentActionDriver = unsupportedPaymentActionDriver;

/** The currently registered driver (the refusing default until one is registered). */
export function getPaymentActionDriver(): MembershipPaymentActionDriver {
  return membershipPaymentActionDriver;
}

/** Register a driver for a test; `null` restores the refusing default. */
export function __setMembershipPaymentActionDriverForTest(
  driver: MembershipPaymentActionDriver | null,
): void {
  membershipPaymentActionDriver = driver ?? unsupportedPaymentActionDriver;
}
